/**
 * 1-D roving tabindex for tile rows and list rows (plan §3.4 · Phase 8 a11y).
 */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type RefCallback,
} from 'react';

type RovingFocusElement = HTMLElement | SVGElement;

/**
 * `'both'` is for widgets laid out in two dimensions but ORDERED in one — the
 * sector treemap and the risk/return scatter. There is no second axis to
 * navigate, so every arrow moves along the single order rather than half the
 * arrows silently doing nothing. A true 2-D widget uses `useGridKeys`.
 */
export type RovingOrientation = 'horizontal' | 'vertical' | 'both';

export interface RovingFocusOptions {
  readonly count: number;
  readonly orientation?: RovingOrientation;
  readonly loop?: boolean;
}

export interface RovingFocusResult<T extends RovingFocusElement> {
  readonly activeIndex: number;
  readonly setActiveIndex: (index: number) => void;
  readonly getTabIndex: (index: number) => 0 | -1;
  readonly getItemRef: (index: number) => RefCallback<T>;
  readonly onKeyDown: (event: KeyboardEvent) => void;
}

const PREV_KEYS: Readonly<Record<RovingOrientation, readonly string[]>> = {
  horizontal: ['ArrowLeft'],
  vertical: ['ArrowUp'],
  both: ['ArrowLeft', 'ArrowUp'],
};

const NEXT_KEYS: Readonly<Record<RovingOrientation, readonly string[]>> = {
  horizontal: ['ArrowRight'],
  vertical: ['ArrowDown'],
  both: ['ArrowRight', 'ArrowDown'],
};

/** -1 / +1 for an arrow this orientation owns, 0 for a key it must not swallow. */
function arrowStep(key: string, orientation: RovingOrientation): -1 | 0 | 1 {
  if (PREV_KEYS[orientation].includes(key)) return -1;
  if (NEXT_KEYS[orientation].includes(key)) return 1;
  return 0;
}

function clampIndex(index: number, count: number, loop: boolean): number {
  if (count <= 0) return 0;
  if (loop) {
    if (index < 0) return count - 1;
    if (index >= count) return 0;
    return index;
  }
  return Math.min(count - 1, Math.max(0, index));
}

function useRovingItemRefs<T extends RovingFocusElement>() {
  const itemRefs = useRef<(T | null)[]>([]);
  const itemRefCallbacks = useRef<RefCallback<T>[]>([]);
  const getItemRef = useCallback((index: number): RefCallback<T> => {
    const existing = itemRefCallbacks.current[index];
    if (existing !== undefined) return existing;
    const itemRef: RefCallback<T> = (element) => {
      itemRefs.current[index] = element;
    };
    itemRefCallbacks.current[index] = itemRef;
    return itemRef;
  }, []);
  const focusItem = useCallback((index: number): void => {
    itemRefs.current[index]?.focus();
  }, []);
  return { getItemRef, focusItem };
}

export function useRovingFocus<T extends RovingFocusElement = HTMLElement>(
  options: RovingFocusOptions,
): RovingFocusResult<T> {
  const { count, orientation = 'horizontal', loop = true } = options;
  const [activeIndex, setActiveIndex] = useState(0);
  const { getItemRef, focusItem } = useRovingItemRefs<T>();

  useEffect(() => {
    setActiveIndex((i) => (count <= 0 ? 0 : Math.min(i, count - 1)));
  }, [count]);

  const moveFocus = useCallback(
    (index: number): void => {
      setActiveIndex(index);
      focusItem(index);
    },
    [focusItem],
  );

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (count <= 0) return;
      const step = arrowStep(event.key, orientation);
      if (step !== 0) {
        event.preventDefault();
        moveFocus(clampIndex(activeIndex + step, count, loop));
        return;
      }
      if (event.key === 'Home') {
        event.preventDefault();
        moveFocus(0);
      } else if (event.key === 'End') {
        event.preventDefault();
        moveFocus(count - 1);
      }
    },
    [activeIndex, count, loop, moveFocus, orientation],
  );

  const getTabIndex = useCallback(
    (index: number): 0 | -1 => (index === activeIndex ? 0 : -1),
    [activeIndex],
  );

  return { activeIndex, setActiveIndex, getTabIndex, getItemRef, onKeyDown };
}
